import React, { useState, useEffect } from 'react';
import { Building, Plus, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import BusinessPartnerFormModal from '../../components/admin/BusinessPartnerFormModal';
import BusinessOfferFormModal from '../../components/admin/BusinessOfferFormModal';
import BusinessPartnersList from '../../components/admin/BusinessPartnersList';
import { useAuth } from '../../lib/auth';
import { GET_BUSINESSES } from '../../graphql/admin/queries/business';
import { DELETE_BUSINESS } from '../../graphql/admin/mutations/business';
import type { 
  Business, 
  BusinessOfferFormModalProps, 
  BusinessPartnerFormModalProps 
} from '../../types/business';
import { getImageUrl } from '../../utils/imageUrl';

// Créer un composant séparé pour l'image du logo
const BusinessLogo = ({ logo, companyName }: { logo: string | null, companyName: string }) => {
  if (!logo) {
    return (
      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
        <Building className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={getImageUrl(logo)}
      alt={companyName}
      className="w-16 h-16 object-contain rounded-lg border border-gray-200"
    />
  );
};

export default function BusinessPartnersPage() {
  const navigate = useNavigate();
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  
  // Récupérer l'utilisateur depuis le localStorage si useAuth ne fonctionne pas
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const { loading: queryLoading, error: queryError, data: queryData, refetch } = useQuery(GET_BUSINESSES, {
    variables: { 
      agencyName: user.agencyName || '' 
    },
    skip: !user.agencyName,
    onError: (error) => {
      console.error('Erreur de requête:', error);
    }
  });

  const [deleteBusiness] = useMutation(DELETE_BUSINESS, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      alert(`Erreur lors de la suppression : ${error.message}`);
    }
  });

  // Rediriger vers la page de connexion si pas d'agence
  useEffect(() => {
    if (!user.agencyName) {
      navigate('/login', { 
        replace: true,
        state: { from: '/admin/business-partners' } 
      });
    }
  }, [user.agencyName, navigate]);

  // Ne pas afficher le composant si pas d'agence
  if (!user.agencyName) {
    return null;
  }

  const handleDeletePartner = async (businessId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ?')) {
      try {
        await deleteBusiness({
          variables: { id: businessId }
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleAddPartner = async (data: any) => {
    try {
      await refetch(); // Rafraîchir la liste après l'ajout/modification
      setShowPartnerModal(false);
      setSelectedBusiness(null);
    } catch (error) {
      console.error('Erreur dans handleAddPartner:', error);
      alert(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  const handleEditBusiness = async (business: Business) => {
    try {
      // S'assurer que l'entreprise a le bon format
      const formattedBusiness: Business = {
        ...business,
        logo: business.logo || null,
        website: business.website || null,
        description: business.description || null
      };
      setSelectedBusiness(formattedBusiness);
      setShowPartnerModal(true);
    } catch (error) {
      console.error('Erreur dans handleEditBusiness:', error);
      alert(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  const handleSendOffer = (business: Business) => {
    setSelectedBusiness(business);
    setShowOfferModal(true);
  };

  const handleNavigateToDetails = (businessId: number) => {
    navigate(`/admin/business-partners/${businessId}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Partenaires commerciaux</h1>
            <p className="text-gray-600">
              Gérez vos partenariats avec les entreprises
            </p>
          </div>
          <button
            onClick={() => setShowPartnerModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle entreprise
          </button>
        </div>

        {queryLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : queryError ? (
          <div className="text-red-600">
            Erreur : {queryError.message}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg">
            {!queryData?.businesses?.length ? (
              <div className="p-6 text-center text-gray-500">
                Aucune entreprise partenaire trouvée
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {queryData.businesses.map((business: Business) => {
                  return (
                    <li key={business.id} className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            <BusinessLogo logo={business.logo} companyName={business.companyName} />
                          </div>

                          <div className="flex-grow">
                            <div className="flex items-center mb-1">
                              <h3 
                                onClick={() => handleNavigateToDetails(business.id)}
                                className="text-lg font-medium text-gray-900 hover:text-blue-600 cursor-pointer transition-colors"
                              >
                                {business.companyName}
                              </h3>
                              <span className="ml-2 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                                {business.sector}
                              </span>
                            </div>

                            <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-gray-500">
                              <div>
                                <p className="font-medium text-gray-900">Contact Principal</p>
                                <p>{business.contactName} {business.contactLastName}</p>
                                <p>{business.contactPosition}</p>
                                <p className="mt-1">
                                  <a 
                                    href={`tel:${business.contactPhone}`}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    {business.contactPhone}
                                  </a>
                                </p>
                                <p>
                                  <a 
                                    href={`mailto:${business.email}`}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    {business.email}
                                  </a>
                                </p>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Adresse</p>
                                <p>{business.address}</p>
                                <p>{business.city}</p>
                                <p>{business.country}</p>
                                {business.website && (
                                  <p className="mt-1">
                                    <a 
                                      href={business.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      Site web
                                    </a>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex-shrink-0 flex space-x-3">
                            <button
                              onClick={() => handleNavigateToDetails(business.id)}
                              className="text-sm px-3 py-1 text-gray-600 hover:text-gray-800 border border-gray-600 rounded-md hover:bg-gray-50"
                            >
                              Détails
                            </button>
                            <button
                              onClick={() => handleSendOffer(business)}
                              className="text-sm px-3 py-1 text-green-600 hover:text-green-800 border border-green-600 rounded-md hover:bg-green-50 flex items-center"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Envoyer une offre
                            </button>
                            <button
                              onClick={() => handleEditBusiness(business)}
                              className="text-sm px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-600 rounded-md hover:bg-blue-50"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={() => handleDeletePartner(business.id)}
                              className="text-sm px-3 py-1 text-red-600 hover:text-red-800 border border-red-600 rounded-md hover:bg-red-50"
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <BusinessPartnerFormModal
          isOpen={showPartnerModal}
          onClose={() => {
            setShowPartnerModal(false);
            setSelectedBusiness(null);
          }}
          onSubmit={handleAddPartner}
          selectedBusiness={selectedBusiness}
          title={selectedBusiness ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
        />

        {selectedBusiness && (
          <BusinessOfferFormModal
            isOpen={showOfferModal}
            onClose={() => {
              setShowOfferModal(false);
              setSelectedBusiness(null);
            }}
            business={selectedBusiness}
          />
        )}
      </div>
    </AdminLayout>
  );
}